import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { v4 as uuid } from 'uuid';
import { MenuOptionLabel } from '~/constants/srp/MenuOption';
import type { TradeInfo } from '~/contracts/srp';

type ContextType = {
  expandOnOpen: MenuOptionLabel;
  regenerateSearchSessionId: () => void;
  resultsSectionRef: React.RefObject<HTMLDivElement>;
  searchSessionId: string;
  setExpandOnOpen: React.Dispatch<React.SetStateAction<MenuOptionLabel>>;
  setShowFinance: React.Dispatch<React.SetStateAction<boolean>>;
  setShowFullGetItBy: React.Dispatch<React.SetStateAction<boolean>>;
  showFinance: boolean;
  showFullGetItBy: boolean;
  tradeInfoFromApis: LoadableState<TradeInfo>;
  utmParams: Record<string, string>;
};

const defaultContext: ContextType = {
  expandOnOpen: MenuOptionLabel.None,
  regenerateSearchSessionId() {},
  resultsSectionRef: { current: null },
  searchSessionId: '',
  setExpandOnOpen() {},
  setShowFinance() {},
  setShowFullGetItBy() {},
  showFinance: false,
  showFullGetItBy: true,
  tradeInfoFromApis: defaultLoadableState<TradeInfo>({}),
  utmParams: {}
};

const SearchContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  searchSessionId?: string;
  showFinance: boolean;
  utmParams: Record<string, string>;
}>;

export function SearchContextProvider(props: OwnProps) {
  const resultsSectionRef = useRef<HTMLDivElement>(null);
  const [expandOnOpen, setExpandOnOpen] = useState(MenuOptionLabel.None);

  const [searchSessionId, setSearchSessionId] = useState(
    props.searchSessionId ?? uuid()
  );
  const [showFullGetItBy, setShowFullGetItBy] = useState(false);
  const [showFinance, setShowFinance] = useState(props.showFinance ?? false);

  const regenerateSearchSessionId = useCallback(() => {
    setSearchSessionId(uuid());
  }, []);

  const tradeInfoFromApis = useLoadableState<TradeInfo>({});

  const value = useMemo<ContextType>(
    (): ContextType => ({
      expandOnOpen,
      regenerateSearchSessionId,
      resultsSectionRef,
      searchSessionId,
      setExpandOnOpen,
      setShowFinance,
      setShowFullGetItBy,
      showFinance,
      showFullGetItBy,
      tradeInfoFromApis,
      utmParams: props.utmParams
    }),
    [
      expandOnOpen,
      props.utmParams,
      regenerateSearchSessionId,
      searchSessionId,
      showFinance,
      showFullGetItBy,
      tradeInfoFromApis
    ]
  );

  return (
    <SearchContext.Provider value={value}>
      {props.children}
    </SearchContext.Provider>
  );
}

SearchContextProvider.displayName = 'SearchContextProvider';

export function useShowFinance() {
  return useContext(SearchContext).showFinance;
}

export function useTradeInfo(): TradeInfo {
  return useContext(SearchContext).tradeInfoFromApis.data;
}

export default SearchContext;
